import React, { useState, useMemo } from "react";
import { Appointment } from "../../services/appointmentService";
import AppointmentService from "../../services/appointmentService";
import ServiceSelectionModal from "./ServiceSelectionModal";

interface SelectedService {
  service: {
    id: number;
    name: string;
    base_fee: number;
    clinic_percentage: number;
    provider_percentage: number;
    owner_percentage: number;
    referrer_percentage: number;
  };
  referrer_name: string;
}

interface AppointmentSidebarProps {
  onDoctorSelection?: {
    selectFirst: () => void;
    selectAll: () => void;
    clearAll: () => void;
  };
  appointments: Appointment[];
  doctors: any[];
  selectedDoctors: number[];
  currentDate: Date;
  onDateChange: (date: Date) => void;
  onDoctorToggle: (doctorId: number) => void;
  onAppointmentClick: (appointment: Appointment) => void;
  onAppointmentAction: (
    action: "confirm" | "arrive" | "complete" | "cancel" | "no_show" | "delete",
    appointmentId: number,
    reason?: string,
    services?: SelectedService[]
  ) => Promise<void>;
  onNewAppointment: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  isLoading: boolean;
  onSaveCustomPricing?: (
    appointmentId: number,
    services: SelectedService[]
  ) => Promise<void>; // ADD THIS
  canToggleDoctors: boolean;
  userRole: string;
}

const AppointmentSidebar: React.FC<AppointmentSidebarProps> = ({
  appointments,
  doctors,
  selectedDoctors,
  currentDate,
  onDateChange,
  onDoctorToggle,
  onAppointmentClick,
  onAppointmentAction,
  onNewAppointment,
  collapsed,
  onToggleCollapse,
  isLoading,
  onSaveCustomPricing,
  canToggleDoctors, // ADDED
  userRole, // ADDED
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [cancelReason, setCancelReason] = useState<{ [key: number]: string }>(
    {}
  );
  const [showCancelInput, setShowCancelInput] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(
    null
  );
  const [isMobile, setIsMobile] = useState(false);
  const [doctorFilterExpanded, setDoctorFilterExpanded] = useState(false);

  // Add state for service selection modal
  const [showServiceSelection, setShowServiceSelection] = useState<{
    appointment: Appointment;
  } | null>(null);

  // Check if mobile on mount and resize
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Get available actions for an appointment
  const getAvailableActions = (appointment: Appointment) => {
    const statuses = AppointmentService.getAppointmentStatuses();
    const currentStatusInfo = statuses[appointment.status] || {};
    return currentStatusInfo.next_statuses || [];
  };

  // Get action button config
  const getActionConfig = (action: string) => {
    const configs: Record<string, any> = {
      confirmed: { label: "Confirm", icon: "✅", style: "success" },
      arrived: { label: "Arrived", icon: "🏥", style: "primary" },
      completed: { label: "Complete", icon: "✔️", style: "secondary" },
      no_show: { label: "No Show", icon: "👻", style: "warning" },
      cancelled: { label: "Cancel", icon: "❌", style: "danger" },
    };
    return configs[action] || { label: action, icon: "📋", style: "secondary" };
  };

  const handleCancelConfirm = (appointmentId: number) => {
    onAppointmentAction("cancel", appointmentId, cancelReason[appointmentId]);
    setShowCancelInput(null);
    setCancelReason((prev) => ({ ...prev, [appointmentId]: "" }));
  };

  const handleCancelCancel = () => {
    setShowCancelInput(null);
    setCancelReason({});
  };

  const handleDeleteConfirm = (appointmentId: number) => {
    onAppointmentAction("delete", appointmentId);
    setShowDeleteConfirm(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(null);
  };

  // Add handler for completing with services
  const handleCompleteWithServices = async (
    selectedServices: SelectedService[]
  ) => {
    if (showServiceSelection) {
      try {
        await onAppointmentAction(
          "complete",
          showServiceSelection.appointment.id,
          undefined, // reason
          selectedServices // services array
        );
        setShowServiceSelection(null);
      } catch (error) {
        console.error("Error completing appointment with services:", error);
        throw error;
      }
    }
  };

  // Filter appointments based on search term
  const filteredAppointments = useMemo(() => {
    if (!searchTerm) return appointments;
    return appointments.filter(
      (appointment) =>
        appointment.patient?.first_name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        appointment.patient?.last_name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        appointment.patient?.phone?.includes(searchTerm) ||
        appointment.doctor?.first_name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        appointment.doctor?.last_name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        appointment.reason?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [appointments, searchTerm]);

  // Group appointments by status
  const groupedAppointments = useMemo(() => {
    const groups: { [key: string]: Appointment[] } = {};
    filteredAppointments.forEach((appointment) => {
      const status = appointment.status || "scheduled";
      if (!groups[status]) {
        groups[status] = [];
      }
      groups[status].push(appointment);
    });
    return groups;
  }, [filteredAppointments]);

  // Sort status groups by priority
  const statusOrder = [
    "scheduled",
    "confirmed",
    "arrived",
    "completed",
    "no_show",
    "cancelled",
  ];
  const sortedGroups = statusOrder.filter(
    (status) => groupedAppointments[status]?.length > 0
  );

  // UPDATED: Filter doctors that are actually selected and available to the user
  const availableDoctors = doctors.filter((doctor) =>
    selectedDoctors.includes(doctor.id)
  );

  // Responsive Styles
  const sidebarStyle: React.CSSProperties = {
    width: collapsed ? "60px" : isMobile ? "100%" : "400px",
    height: isMobile ? "auto" : "100vh",
    background: "#ffffff",
    borderRight: isMobile ? "none" : "1px solid #e5e7eb",
    display: "flex",
    flexDirection: "column",
    transition: "width 0.3s ease",
    overflow: "hidden",
    maxHeight: isMobile ? "80vh" : "100vh",
    maxWidth: isMobile ? "100vw" : "400px",
    overflowX: "hidden",
  };

  const headerStyle: React.CSSProperties = {
    padding: isMobile ? "12px 16px" : "16px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "#f9fafb",
    flexShrink: 0,
  };

  const contentStyle: React.CSSProperties = {
    flex: 1,
    overflow: "auto",
    padding: collapsed ? "8px" : isMobile ? "12px 16px" : "16px",
    height: isMobile ? "auto" : "calc(100vh - 80px)",
    overflowY: "auto",
    overflowX: "hidden",
    width: "100%",
    maxWidth: "100%",
  };

  const appointmentItemStyle: React.CSSProperties = {
    padding: isMobile ? "10px" : "12px",
    margin: "8px 0",
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden",
  };

  const actionButtonStyle = (
    variant:
      | "primary"
      | "secondary"
      | "success"
      | "danger"
      | "warning" = "secondary"
  ): React.CSSProperties => {
    const variants = {
      primary: { bg: "#3b82f6", text: "#ffffff", hover: "#2563eb" },
      secondary: { bg: "#f3f4f6", text: "#374151", hover: "#e5e7eb" },
      success: { bg: "#10b981", text: "#ffffff", hover: "#059669" },
      danger: { bg: "#ef4444", text: "#ffffff", hover: "#dc2626" },
      warning: { bg: "#f59e0b", text: "#ffffff", hover: "#d97706" },
    };

    const colors = variants[variant];

    return {
      padding: isMobile ? "6px 10px" : "4px 8px",
      fontSize: isMobile ? "12px" : "11px",
      fontWeight: "500",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      transition: "all 0.2s ease",
      background: colors.bg,
      color: colors.text,
      display: "flex",
      alignItems: "center",
      gap: "4px",
      minHeight: isMobile ? "36px" : "auto",
    };
  };

  const groupHeaderStyle: React.CSSProperties = {
    fontSize: isMobile ? "13px" : "14px",
    fontWeight: "600",
    color: "#374151",
    marginTop: "16px",
    marginBottom: "8px",
    padding: isMobile ? "6px 10px" : "8px 12px",
    background: "#f9fafb",
    borderRadius: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: isMobile ? "10px 12px" : "8px 12px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    fontSize: "14px",
    outline: "none",
    transition: "border-color 0.2s ease",
    minHeight: isMobile ? "44px" : "auto",
  };

  const buttonStyle = (
    variant: "primary" | "secondary" = "secondary"
  ): React.CSSProperties => ({
    width: "100%",
    padding: isMobile ? "12px" : "10px 12px",
    background: variant === "primary" ? "#3b82f6" : "#f3f4f6",
    color: variant === "primary" ? "#ffffff" : "#374151",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    marginBottom: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    minHeight: isMobile ? "44px" : "auto",
    transition: "all 0.2s ease",
  });

  if (collapsed) {
    return (
      <div style={sidebarStyle}>
        <div style={headerStyle}>
          <button
            onClick={onToggleCollapse}
            style={{
              background: "none",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
              padding: "4px",
            }}
            title="Expand sidebar"
          >
            ▶️
          </button>
        </div>
        <div style={contentStyle}>
          <button
            onClick={onNewAppointment}
            style={{
              width: "100%",
              padding: "8px",
              background: "#3b82f6",
              color: "#ffffff",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "18px",
              marginBottom: "16px",
            }}
            title="New Appointment"
          >
            +
          </button>
          <div
            style={{ textAlign: "center", fontSize: "12px", color: "#6b7280" }}
          >
            {appointments.length} appointments
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={sidebarStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div>
          <h2
            style={{
              margin: 0,
              fontSize: isMobile ? "16px" : "18px",
              fontWeight: "600",
              color: "#1f2937",
            }}
          >
            {isMobile ? "Today" : "Appointments"}
          </h2>
          <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "2px" }}>
            {currentDate.toLocaleDateString("en-US", {
              weekday: isMobile ? "short" : "long",
              year: "numeric",
              month: isMobile ? "short" : "long",
              day: "numeric",
            })}
          </div>
          {/* ADDED: Show user role in sidebar header */}
          <div
            style={{
              fontSize: "11px",
              color: "#9ca3af",
              marginTop: "2px",
              fontStyle: "italic",
            }}
          >
            {userRole}
          </div>
        </div>
        {!isMobile && (
          <button
            onClick={onToggleCollapse}
            style={{
              background: "none",
              border: "none",
              fontSize: "16px",
              cursor: "pointer",
              padding: "4px",
            }}
            title="Collapse sidebar"
          >
            ◀️
          </button>
        )}
      </div>

      {/* Content */}
      <div style={contentStyle}>
        {/* New Appointment Button */}

        <button onClick={onNewAppointment} style={buttonStyle("primary")}>
          ➕ New Appointment
        </button>

        {/* UPDATED: Doctor Filter Section - Only show if user can toggle doctors */}
        {canToggleDoctors && doctors.length > 1 && (
          <div style={{ marginBottom: "16px" }}>
            <button
              onClick={() => setDoctorFilterExpanded(!doctorFilterExpanded)}
              style={{
                width: "100%",
                padding: isMobile ? "12px 16px" : "10px 14px",
                background: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "500",
                color: "#374151",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "8px",
                minHeight: isMobile ? "44px" : "auto",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.background = "#f3f4f6";
                }
              }}
              onMouseLeave={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.background = "#f9fafb";
                }
              }}
            >
              <div>
                <span style={{ fontWeight: "600" }}>Filter Doctors</span>
                <div
                  style={{
                    fontSize: "11px",
                    color: "#6b7280",
                    marginTop: "2px",
                  }}
                >
                  {selectedDoctors.length} of {doctors.length} selected
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span
                  style={{
                    background: "#3b82f6",
                    color: "#ffffff",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    fontSize: "11px",
                    fontWeight: "600",
                  }}
                >
                  {selectedDoctors.length}
                </span>
                <span
                  style={{
                    transform: doctorFilterExpanded
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: "transform 0.2s",
                    fontSize: "14px",
                  }}
                >
                  ▼
                </span>
              </div>
            </button>

            {doctorFilterExpanded && (
              <div
                style={{
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: isMobile ? "16px" : "14px",
                  marginBottom: "12px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                }}
              >
                {/* Quick actions */}
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    marginBottom: "12px",
                  }}
                >
                  <button
                    onClick={() => {
                      // Select first doctor only by using the parent's handleDoctorToggle
                      const firstDoctor = [...doctors].sort(
                        (a, b) => a.id - b.id
                      )[0];
                      // Clear current selections and select only first doctor
                      const firstDoctorId = firstDoctor.id;
                      // This will trigger parent's handleDoctorToggle to set only first doctor
                      window.dispatchEvent(
                        new CustomEvent("selectFirstDoctor", {
                          detail: { doctorId: firstDoctorId },
                        })
                      );
                    }}
                    style={{
                      padding: "6px 12px",
                      background: "#ffffff",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "11px",
                      cursor: "pointer",
                      color: "#374151",
                      transition: "all 0.2s ease",
                      flex: 1,
                    }}
                  >
                    First Only
                  </button>
                  {/* <button
                    onClick={() => onDoctorSelection?.selectFirst()}
                    style={{
                      padding: "6px 12px",
                      background: "#ffffff",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "11px",
                      cursor: "pointer",
                      color: "#374151",
                      transition: "all 0.2s ease",
                      flex: 1,
                    }}
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => setSelectedDoctors([])}
                    style={{
                      padding: "6px 12px",
                      background: "#ffffff",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "11px",
                      cursor: "pointer",
                      color: "#374151",
                      transition: "all 0.2s ease",
                      flex: 1,
                    }}
                  >
                    Clear
                  </button> */}
                </div>

                {/* Doctor checkboxes - sorted by ID */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: isMobile ? "12px" : "10px",
                    maxHeight: isMobile ? "200px" : "250px",
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  {[...doctors]
                    .sort((a, b) => a.id - b.id)
                    .map((doctor, index) => (
                      <label
                        key={doctor.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          fontSize: "13px",
                          cursor: "pointer",
                          padding: "8px 12px",
                          borderRadius: "6px",
                          background: selectedDoctors.includes(doctor.id)
                            ? "#eff6ff"
                            : "#ffffff",
                          border: selectedDoctors.includes(doctor.id)
                            ? "1px solid #bfdbfe"
                            : "1px solid #e5e7eb",
                          transition: "all 0.2s ease",
                          position: "relative",
                        }}
                        onMouseEnter={(e) => {
                          if (
                            !isMobile &&
                            !selectedDoctors.includes(doctor.id)
                          ) {
                            e.currentTarget.style.background = "#f8fafc";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (
                            !isMobile &&
                            !selectedDoctors.includes(doctor.id)
                          ) {
                            e.currentTarget.style.background = "#ffffff";
                          }
                        }}
                      >
                        {/* Priority indicator for first doctor */}
                        {index === 0 && (
                          <span
                            style={{
                              position: "absolute",
                              top: "-4px",
                              right: "-4px",
                              background: "#10b981",
                              color: "#ffffff",
                              fontSize: "8px",
                              padding: "2px 4px",
                              borderRadius: "4px",
                              fontWeight: "600",
                            }}
                          >
                            DEFAULT
                          </span>
                        )}

                        <input
                          type={isMobile ? "radio" : "checkbox"}
                          name="sidebar_doctor_selection"
                          checked={selectedDoctors.includes(doctor.id)}
                          onChange={() => {
                            if (isMobile) {
                              // Mobile: Use parent's setSelectedDoctors to select only this doctor
                              window.dispatchEvent(
                                new CustomEvent("selectSingleDoctor", {
                                  detail: { doctorId: doctor.id },
                                })
                              );
                            } else {
                              // Desktop: Keep existing toggle behavior
                              onDoctorToggle(doctor.id);
                            }
                          }}
                          style={{
                            minWidth: isMobile ? "18px" : "16px",
                            minHeight: isMobile ? "18px" : "16px",
                            accentColor: "#3b82f6",
                          }}
                        />

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontWeight: "500",
                              color: selectedDoctors.includes(doctor.id)
                                ? "#1e40af"
                                : "#1f2937",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Dr. {doctor.first_name} {doctor.last_name}
                          </div>
                          {doctor.specialty && (
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#6b7280",
                                marginTop: "2px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {doctor.specialty}
                            </div>
                          )}
                        </div>
                      </label>
                    ))}
                </div>

                {/* Summary */}
                <div
                  style={{
                    marginTop: "12px",
                    padding: "8px 12px",
                    background: "#ffffff",
                    borderRadius: "6px",
                    fontSize: "11px",
                    color: "#6b7280",
                    textAlign: "center",
                    border: "1px solid #e5e7eb",
                  }}
                >
                  💡 Tip: Start with first doctor, add more as needed
                </div>
              </div>
            )}
          </div>
        )}

        {/* ADDED: Show current doctor filter status for restricted users */}
        {!canToggleDoctors && availableDoctors.length > 0 && (
          <div
            style={{
              marginBottom: "16px",
              padding: "10px 12px",
              background: canToggleDoctors ? "#f0f9ff" : "#fef3c7",
              border: `1px solid ${canToggleDoctors ? "#bfdbfe" : "#fbbf24"}`,
              borderRadius: "6px",
              fontSize: "12px",
              color: canToggleDoctors ? "#1e40af" : "#92400e",
            }}
          >
            <div
              style={{
                fontWeight: "500",
                marginBottom: "6px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              {canToggleDoctors ? "🎛️" : "👤"} Current View
            </div>
            <div style={{ fontSize: "11px", lineHeight: "1.4" }}>
              {availableDoctors.length === 1 ? (
                <>
                  <strong>Dr. {availableDoctors[0].name}</strong>
                  {availableDoctors[0].specialty && (
                    <div style={{ marginTop: "2px" }}>
                      📋 {availableDoctors[0].specialty}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <strong>{availableDoctors.length} doctors selected</strong>
                  <div style={{ marginTop: "4px" }}>
                    {availableDoctors.slice(0, 2).map((doctor, idx) => (
                      <div key={doctor.id} style={{ fontSize: "10px" }}>
                        • Dr. {doctor.first_name}{" "}
                        {doctor.specialty && `(${doctor.specialty})`}
                      </div>
                    ))}
                    {availableDoctors.length > 2 && (
                      <div style={{ fontSize: "10px", fontStyle: "italic" }}>
                        + {availableDoctors.length - 2} more...
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Date Picker */}
        <div style={{ marginBottom: "16px" }}>
          <label
            htmlFor="current_date1"
            style={{
              display: "block",
              fontSize: "12px",
              fontWeight: "500",
              color: "#374151",
              marginBottom: "6px",
            }}
          >
            Select Date
          </label>
          <input
            type="date"
            id="current_date1"
            name="current_date1"
            value={currentDate.toISOString().split("T")[0]}
            onChange={(e) => onDateChange(new Date(e.target.value))}
            style={inputStyle}
          />
        </div>

        {/* Search */}
        <div style={{ marginBottom: "16px" }}>
          <input
            type="text"
            id="search_appointments"
            name="search_appointments"
            placeholder="Search appointments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={inputStyle}
          />
        </div>

        {/* Loading State */}
        {isLoading && (
          <div
            style={{
              textAlign: "center",
              padding: "20px",
              color: "#6b7280",
            }}
          >
            Loading appointments...
          </div>
        )}

        {/* Appointments List - Grouped by Status */}
        {!isLoading && (
          <>
            {sortedGroups.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: isMobile ? "20px 10px" : "40px 20px",
                  color: "#6b7280",
                }}
              >
                <div
                  style={{
                    fontSize: isMobile ? "36px" : "48px",
                    marginBottom: "16px",
                  }}
                >
                  📅
                </div>
                <div
                  style={{
                    fontSize: isMobile ? "14px" : "16px",
                    fontWeight: "500",
                    marginBottom: "8px",
                  }}
                >
                  No appointments found
                </div>
                <div style={{ fontSize: isMobile ? "12px" : "14px" }}>
                  {searchTerm
                    ? "Try adjusting your search terms"
                    : isMobile
                    ? 'Tap "Book Appointment" to add one'
                    : 'Click "New Appointment" to get started'}
                </div>
              </div>
            ) : (
              sortedGroups.map((status) => {
                const statusInfo = AppointmentService.getStatusInfo(status);
                const statusAppointments = groupedAppointments[status];

                return (
                  <div key={status}>
                    {/* Status Group Header */}
                    <div style={groupHeaderStyle}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <span>{statusInfo.icon}</span>
                        <span>{statusInfo.label}</span>
                        <span
                          style={{
                            background: statusInfo.color + "20",
                            color: statusInfo.color,
                            padding: "2px 8px",
                            borderRadius: "12px",
                            fontSize: "11px",
                            fontWeight: "500",
                          }}
                        >
                          {statusAppointments.length}
                        </span>
                      </div>
                    </div>

                    {/* Appointments in this status */}
                    {statusAppointments.map((appointment) => (
                      <div
                        key={appointment.id}
                        style={{
                          ...appointmentItemStyle,
                          borderLeft: `4px solid ${appointment.status_color}`,
                        }}
                        onClick={() => onAppointmentClick(appointment)}
                      >
                        {/* Patient Info */}
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            marginBottom: "8px",
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: isMobile ? "14px" : "14px",
                                fontWeight: "600",
                                color: "#1f2937",
                                marginBottom: "2px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {appointment.patient?.first_name}{" "}
                              {appointment.patient?.last_name}
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#6b7280",
                              }}
                            >
                              📞 {appointment.patient?.phone}
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: "14px",
                              fontWeight: "500",
                              color: "#1f2937",
                              marginLeft: "8px",
                            }}
                          >
                            {appointment.formatted_time}
                          </div>
                        </div>
                        {appointment.has_custom_pricing && (
                          <div
                            style={{
                              background: "#fef3c7",
                              color: "#92400e",
                              padding: "2px 6px",
                              borderRadius: "4px",
                              fontSize: "10px",
                              fontWeight: "600",
                              marginTop: "4px",
                              marginBottom: "4px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>
                              💲 Custom: ${appointment.amount_to_collect}
                            </span>
                            {appointment.original_total_fee && (
                              <span
                                style={{
                                  textDecoration: "line-through",
                                  opacity: 0.6,
                                  fontSize: "9px",
                                }}
                              >
                                ${appointment.original_total_fee}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Doctor & Type Info - existing code */}
                        <div
                          style={
                            {
                              /* existing styles */
                            }
                          }
                        >
                          👨‍⚕️ Dr. {appointment.doctor?.first_name}{" "}
                          {appointment.doctor?.last_name}
                          {/* rest of existing content */}
                        </div>

                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: appointment.custom_pricing_applied
                              ? "#f59e0b"
                              : "#10b981",
                            marginBottom: "4px",
                            padding: "4px 8px",
                            background: appointment.custom_pricing_applied
                              ? "#fef3c7"
                              : "#ecfdf5",
                            borderRadius: "4px",
                            textAlign: "center",
                          }}
                        >
                          {appointment.custom_pricing_applied
                            ? "COLLECT: $"
                            : "Standard: $"}
                          {appointment.total_fee || 0}
                        </div>

                        {/* Show pricing reason if exists */}
                        {appointment.custom_pricing_reason && (
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6b7280",
                              fontStyle: "italic",
                              marginBottom: "4px",
                            }}
                          >
                            Reason: {appointment.custom_pricing_reason}
                          </div>
                        )}

                        {/* Show pricing reason if exists */}
                        {appointment.custom_pricing_reason && (
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6b7280",
                              fontStyle: "italic",
                              marginBottom: "4px",
                            }}
                          >
                            Reason: {appointment.custom_pricing_reason}
                          </div>
                        )}
                        {/* Doctor & Type Info */}
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            marginBottom: "8px",
                          }}
                        >
                          👨‍⚕️ Dr. {appointment.doctor?.first_name}{" "}
                          {appointment.doctor?.last_name}
                          {appointment.reason && (
                            <div
                              style={{
                                marginTop: "4px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              📋 {appointment.reason}
                            </div>
                          )}
                        </div>

                        {/* Status Indicator with better styling */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginTop: "8px",
                            flexWrap: "wrap",
                          }}
                        >
                          <span
                            style={{
                              padding: "2px 8px",
                              borderRadius: "12px",
                              fontSize: "11px",
                              fontWeight: "500",
                              backgroundColor: appointment.status_color + "20",
                              color: appointment.status_color,
                              border: `1px solid ${appointment.status_color}40`,
                            }}
                          >
                            {
                              AppointmentService.getStatusInfo(
                                appointment.status
                              ).icon
                            }{" "}
                            {appointment.status_label}
                          </span>

                          {appointment.type && (
                            <span
                              style={{
                                padding: "2px 6px",
                                borderRadius: "8px",
                                fontSize: "10px",
                                backgroundColor: "#f3f4f6",
                                color: "#6b7280",
                              }}
                            >
                              {appointment.type_label}
                            </span>
                          )}
                        </div>

                        {/* Status Action Buttons */}
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "4px",
                            marginTop: "8px",
                          }}
                        >
                          {getAvailableActions(appointment).map(
                            (action: string) => {
                              const config = getActionConfig(action);

                              if (action === "cancelled") {
                                // Special handling for cancel action
                                if (showCancelInput === appointment.id) {
                                  return (
                                    <div
                                      key="cancel-input"
                                      style={{
                                        width: "100%",
                                        marginTop: "8px",
                                      }}
                                    >
                                      <input
                                        type="text"
                                        id={`cancel_reason_${appointment.id}`}
                                        name={`cancel_reason_${appointment.id}`}
                                        placeholder="Reason (optional)"
                                        value={
                                          cancelReason[appointment.id] || ""
                                        }
                                        onChange={(e) =>
                                          setCancelReason((prev) => ({
                                            ...prev,
                                            [appointment.id]: e.target.value,
                                          }))
                                        }
                                        style={{
                                          ...inputStyle,
                                          marginBottom: "4px",
                                          fontSize: "12px",
                                          padding: "6px 8px",
                                        }}
                                        autoFocus
                                      />

                                      <div
                                        style={{ display: "flex", gap: "4px" }}
                                      >
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleCancelConfirm(appointment.id);
                                          }}
                                          style={{
                                            ...actionButtonStyle("danger"),
                                            flex: 1,
                                          }}
                                        >
                                          {config.icon} Cancel
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleCancelCancel();
                                          }}
                                          style={{
                                            ...actionButtonStyle("secondary"),
                                            flex: 1,
                                          }}
                                        >
                                          Back
                                        </button>
                                      </div>
                                    </div>
                                  );
                                }

                                return (
                                  <button
                                    key={action}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowCancelInput(appointment.id);
                                    }}
                                    style={actionButtonStyle(config.style)}
                                    title={`${config.label} Appointment`}
                                  >
                                    {config.icon} {config.label}
                                  </button>
                                );
                              }

                              // Special handling for complete action
                              if (action === "completed") {
                                return (
                                  <button
                                    key={action}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowServiceSelection({ appointment });
                                    }}
                                    style={actionButtonStyle(config.style)}
                                    title="Complete with Services"
                                  >
                                    💰 {config.label}
                                  </button>
                                );
                              }

                              // Handle other status actions
                              const actionMap: Record<string, any> = {
                                confirmed: "confirm",
                                arrived: "arrive",
                                no_show: "no_show",
                              };

                              return (
                                <button
                                  key={action}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onAppointmentAction(
                                      actionMap[action],
                                      appointment.id
                                    );
                                  }}
                                  style={actionButtonStyle(config.style)}
                                  title={`Mark as ${config.label}`}
                                >
                                  {config.icon} {config.label}
                                </button>
                              );
                            }
                          )}

                          {/* Delete Button - only if not completed */}
                          {appointment.status !== "completed" && (
                            <>
                              {showDeleteConfirm === appointment.id ? (
                                <div
                                  style={{ width: "100%", marginTop: "8px" }}
                                >
                                  <div
                                    style={{
                                      padding: "8px 12px",
                                      background: "#fef2f2",
                                      border: "1px solid #fecaca",
                                      borderRadius: "4px",
                                      fontSize: "12px",
                                      color: "#dc2626",
                                      marginBottom: "8px",
                                      textAlign: "center",
                                    }}
                                  >
                                    ⚠️ This will permanently delete the
                                    appointment
                                  </div>
                                  <div style={{ display: "flex", gap: "4px" }}>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteConfirm(appointment.id);
                                      }}
                                      style={{
                                        ...actionButtonStyle("danger"),
                                        flex: 1,
                                      }}
                                    >
                                      🗑️ Delete
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteCancel();
                                      }}
                                      style={{
                                        ...actionButtonStyle("secondary"),
                                        flex: 1,
                                      }}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowDeleteConfirm(appointment.id);
                                  }}
                                  style={actionButtonStyle("danger")}
                                  title="Delete Appointment"
                                >
                                  🗑️ Delete
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })
            )}
          </>
        )}
      </div>

      {/* Service Selection Modal */}
      {showServiceSelection && (
        <ServiceSelectionModal
          isOpen={!!showServiceSelection}
          appointment={showServiceSelection.appointment}
          onClose={() => setShowServiceSelection(null)}
          onComplete={handleCompleteWithServices}
          onSaveCustomPricing={onSaveCustomPricing}
        />
      )}
    </div>
  );
};

export default AppointmentSidebar;
